import React from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useScrollTrigger,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

const navItems = [
  { label: 'About', href: '#about' },
  { label: 'Experience', href: '#experience' },
  { label: 'Skills', href: '#skills' },
  { label: 'Education', href: '#education' },
  { label: 'Projects', href: '#projects' },
  { label: 'Contact', href: '#contact' },
];

function HideOnScroll({ children }: { children: React.ReactElement }) {
  const trigger = useScrollTrigger();
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Navbar: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setDrawerOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <HideOnScroll>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            background: isMobile
              ? 'rgba(250, 246, 238, 0.95)'
              : scrolled
                ? 'rgba(250, 246, 238, 0.92)'
                : 'transparent',
            WebkitBackdropFilter: isMobile || scrolled ? 'blur(12px)' : 'none',
            backdropFilter: isMobile || scrolled ? 'blur(12px)' : 'none',
            borderBottom: isMobile || scrolled ? '1px solid rgba(200, 162, 77, 0.1)' : 'none',
            transition: 'all 0.3s ease',
          }}
        >
          <Toolbar
            sx={{
              maxWidth: '1100px',
              width: '100%',
              mx: 'auto',
              py: 1,
              justifyContent: isMobile ? 'flex-end' : 'center',
            }}
          >

            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {navItems.map((item) => (
                  <Button
                    key={item.label}
                    onClick={() => handleNavClick(item.href)}
                    sx={{
                      color: '#2A231C',
                      fontWeight: 500,
                      fontSize: '0.88rem',
                      fontFamily: '"Kanit", sans-serif',
                      px: 2,
                      borderRadius: '6px',
                      '&:hover': {
                        color: '#C8A24D',
                        background: 'rgba(200, 162, 77, 0.06)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            )}

            {isMobile && (
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{ color: '#2A231C' }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>
      </HideOnScroll>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            background: '#FAF6EE',
            borderLeft: '1px solid rgba(200, 162, 77, 0.1)',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            onClick={() => setDrawerOpen(false)}
            sx={{ color: '#2A231C' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {navItems.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                onClick={() => handleNavClick(item.href)}
                sx={{
                  py: 1.5,
                  px: 3,
                  '&:hover': {
                    background: 'rgba(200, 162, 77, 0.06)',
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontFamily: '"Kanit", sans-serif',
                    fontWeight: 500,
                    fontSize: '1.05rem',
                    color: '#2A231C',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </>
  );
};

export default Navbar;
